import "./AmFundHome.scss";
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import newRequest from '../../../utils/newRequest';
import { ScaleLoader } from "react-spinners";
import { motion } from "framer-motion";
import CircularButton, { CustomLoadingButton } from '../../../components/ui/CustomButton';
import { MdAdd, MdArrowBackIos, MdClose, MdOutlineRefresh } from "react-icons/md";
import { CiImageOn } from "react-icons/ci";
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import TagIcon from '@mui/icons-material/Tag';
import { GrMoney } from "react-icons/gr";
import WorkedOnBy from '../../../components/workedOnBy/WorkedOnBy';
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import VideoPlayer from '../../../components/videoPlayer/VideoPlayer';
import { FiSearch } from 'react-icons/fi';
import { IoDocumentTextSharp } from "react-icons/io5";
import OnScreenReports from "../../../components/onScreenReports/OnScreenReports";

const AmFundHome = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [searchTerm, setSearchTerm] = useState("");
    const location = useLocation();
    const [reportGroupArray, setReportKeysArray] = useState(["am-securities"]);
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
    const showBack = location.state?.showBack || "N";
    const [funds, setFunds] = useState([]);


    const handleCloseNotifyDialog = () => {
        setIsNotifyDialogOpen(false);
    };

    const handleOpenOverlay = () => {
        setIsOverlayOpen(true);
    };

    const fetchFunds = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.post("/fund/findAll", { result_limit: 3 });
            setFunds(res.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFunds();
    }, []);

    const handleSearchFunds = async () => {
        try {
            if (searchTerm) {
                setIsLoading(true);
                const res = await newRequest.post(
                    "/fund/search",
                    {
                        search_input: searchTerm,
                        limit: 20,
                    }
                );
                setIsLoading(true);
                setFunds(res.data);
            } else {
                fetchFunds();
            }
        } catch (error) {
            console.error("Error searching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div
            className="am-fund-home"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="am-fund-main-wrapper">
                    <motion.div className="list-wrapper"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, transition: { duration: 1 } }}
                        exit={{ opacity: 0, transition: { duration: 1 } }}>
                        <div className="list-navbar">
                            <div className="list-navbar-left">
                                <span className="main-title">{t('funds')}</span>
                            </div>
                            <div className="list-navbar-right">
                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType="left"
                                    endIcon={<MdOutlineRefresh />} onClick={fetchFunds}>
                                    {t('refresh')}
                                </CustomLoadingButton>
                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType="none"
                                    endIcon={<IoDocumentTextSharp />}
                                    onClick={() => setReportsDialogOpen(true)}>
                                    {t('reports')}
                                </CustomLoadingButton>
                                <CustomLoadingButton
                                    endIcon={<MdAdd />}
                                    iconPosition="end"
                                    borderRadiusType="right"
                                    currentTheme={currentTheme}
                                    onClick={() => {
                                        try {
                                            navigate(`/fund/new`);
                                        } catch (error) {
                                            console.log(error)
                                        }
                                    }}
                                >
                                    {t('newFund')}
                                </CustomLoadingButton>
                                {/* This part shows if the menu was opened from home screen */}
                                {showBack === "Y" && <hr className="separator-v" />}
                                {showBack === "Y" &&
                                    <span className="back-icon" onClick={() => navigate(-1)}>
                                        <MdArrowBackIos size={24} />
                                    </span>}
                            </div>
                        </div>
                        <div className="list-content">
                            <GrMoney className="icon" />
                            <span className="title">{t('fundHomeTitle')}</span>
                            <p>{t('fundHomeDesc')}</p>
                            <div className="search-input-wrapper" style={{ width: "50%" }}>
                                <FiSearch className="search-input-icon" />
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder={`${t('search')} ${t('funds')}...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSearchFunds();
                                    }}
                                />
                                {searchTerm?.length > 0 && (
                                    <MdClose
                                        onClick={(e) => {
                                            try {
                                                fetchFunds();
                                                setSearchTerm("");
                                            } catch (error) {
                                                console.log(error);
                                            }
                                        }}
                                        className="search-input-icon"
                                    />
                                )}
                                {searchTerm?.length === 0 && (
                                    <span className="search-input-icon" />
                                )}
                            </div>
                            {searchTerm?.length > 0 && funds?.length > 0 && <span className="sub-title" style={{ marginTop: "10px" }}>{`fetched ${funds?.length} records`}</span>}
                            {funds?.length > 0 &&
                                <div className="cards-wrapper">
                                    {funds?.map((row) => (
                                        <div className="single-card"
                                            key={row?._id}
                                            onClick={() => {
                                                try {
                                                    navigate(`/fund/${row?._id}`);
                                                } catch (error) {
                                                    console.log(error)
                                                }
                                            }}
                                        >
                                            <div className="single-card-top">
                                                <div className="single-card-top-left">
                                                    {row?.fund_logo_url && <img
                                                        src={row?.fund_logo_url}
                                                        alt=""
                                                        className="img"
                                                    />}
                                                    {!row?.fund_logo_url && <CiImageOn style={{ width: "60px", height: "60px" }} />}
                                                </div>
                                                <div className="single-card-top-right">
                                                    <span className="fund-full-name">{row?.fund_name}</span>
                                                    <span className="fund-type">{`${row?.fund_alias}`}</span>
                                                </div>
                                            </div>
                                            <div className="single-card-center">
                                                <div className="single-card-center-row">
                                                    <span className="fund-title">{t('type')}</span>
                                                    <span className="fund-title">{t('status')}</span>
                                                </div>
                                                <div className="single-card-center-row">
                                                    <span className="fund-title-value">{row?.fund_type}</span>
                                                    <span className="fund-title-value">{row?.fund_status}</span>
                                                    {/* <span className={`status ${row?.ent_status}`}>{row?.ent_status}</span> */}
                                                </div>
                                                <div className="single-card-center-col">
                                                    <div className="email-phone-row">
                                                        <TagIcon className="icon" /> <span>{row?.base_currency}</span>
                                                    </div>
                                                    <div className="email-phone-row">
                                                        <LocalPhoneIcon className="icon" /> <span>{row?.investment_horizon}</span>
                                                    </div>
                                                    <div className="email-phone-row">
                                                        <EmailIcon className="icon" /> <span>{row?.minimum_subscription}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="single-card-bottom">
                                                <WorkedOnBy name={row?.updated_by_name_xx} photoUrl={row?.updated_by_photo_url_xx} time_ago_xx={row?.time_ago_xx} />
                                            </div>
                                        </div>
                                    ))}
                                </div>}
                            <div className="list-footer">
                                <button className="overlay-button" onClick={handleOpenOverlay}>
                                    {t('how')}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t('reports')}</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={reportGroupArray}
                                documentData={{
                                    docNo: "1",
                                    docId: "1",
                                    docType: "AllClaim",
                                    format: "xlsx",
                                    entity_code: "",
                                    date_fm: "",
                                    date_to: "",
                                    status: "",
                                }} />
                        </div>
                    </div>
                </motion.div>
            )}
            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={handleCloseNotifyDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton onClick={handleCloseNotifyDialog}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {isOverlayOpen && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <span>{t('help').toUpperCase()}</span>
                            <CircularButton currentTheme={currentTheme} onClick={() => setIsOverlayOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <VideoPlayer videoUrl="https://www.youtube.com/embed/PPCfDe8TfJQ" />
                        </div>
                        <div className="overlay-actions">
                        </div>
                    </div>
                </motion.div>)}
        </motion.div >
    );
}

export default AmFundHome